import type { SwarmAttack } from '../../../data/full/veilleur';
import { angleOf } from '../../../shared/angle';
import { wrap } from '../../../shared/scalar';
import { distanceSquared } from '../../../shared/vec';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy, keepWhere } from '../../effects';
import type { EnemyState, PlayerState, PropState, SimState, Vec2 } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { hourOf, nightLit } from '../hour';
import { closeIn, flyBack, ownerOf } from '../../props';

export const FIREFLY = 'firefly';

export const swarm: AttackModule<SwarmAttack> = {
  reads: ['attackCooldownMul', 'damageMul', 'pierceAdd', 'projectileCountAdd'],

  ammunition: true,

  step(ctx, player, attack, input) {
    const { state } = ctx;
    const count = swarmSize(ctx, player, attack);
    const fireflies = keepSwarm(state, player, count);
    const prey = input.fire ? preyOf(state.enemies, player, attack.reach) : [];
    const resting = fireflies.filter((firefly) => firefly.phase === 'held');
    if (!pullTrigger(player, prey.length > 0 && resting.length > 0, attack.cooldownTicks)) {
      return;
    }
    const pull = Math.floor(
      state.tick / statValue(player, 'attackCooldownMul', attack.cooldownTicks),
    );
    const sent = Math.floor((pull * count) / attack.turnBeats);
    const leaving = Math.floor(((pull + 1) * count) / attack.turnBeats) - sent;
    const first = sent % count;
    resting.sort((a, b) => turnOf(a.rank ?? 0, first, count) - turnOf(b.rank ?? 0, first, count));
    const damage = statValue(player, 'damageMul', attack.damage);
    const coming = stingsComing(fireflies);
    resting.slice(0, leaving).forEach((firefly, index) => {
      const target =
        prey.find((enemy) => (coming.get(enemy.id) ?? 0) < enemy.hp) ?? prey[index % prey.length];
      if (target === undefined) {
        return;
      }
      coming.set(target.id, (coming.get(target.id) ?? 0) + damage);
      firefly.phase = 'flying';
      firefly.x = firefly.prevX = player.x;
      firefly.y = firefly.prevY = player.y;
      firefly.targetId = target.id;
      firefly.ticksLeft = flightTicks(attack.reach, attack.speed);
      firefly.damage = damage;
      firefly.hitsLeft = statValue(player, 'pierceAdd', attack.hits);
      firefly.hitIds = [];
    });
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state } = ctx;
    for (const prop of state.props ?? []) {
      if (prop.kind !== FIREFLY) {
        continue;
      }
      const owner = ownerOf(state, prop);
      const attack = attackOf(ctx, owner);
      if (prop.phase === 'held') {
        prop.x = owner.x;
        prop.y = owner.y;
      } else if (prop.phase === 'flying') {
        sting(ctx, prop, attack);
      } else {
        flyBack(prop, owner, attack.returnSpeed);
      }
    }
  },

  reach(attack) {
    return attack.reach;
  },
};

function swarmSize(
  ctx: Pick<StepContext, 'set' | 'state'>,
  player: PlayerState,
  attack: SwarmAttack,
): number {
  return (
    statValue(player, 'projectileCountAdd', attack.fireflies) + nightLit(attack.night, hourOf(ctx))
  );
}

function firefliesOf(state: SimState, player: Pick<PlayerState, 'id'>): PropState[] {
  return (state.props ?? []).filter((prop) => prop.kind === FIREFLY && prop.playerId === player.id);
}

function keepSwarm(state: SimState, player: PlayerState, count: number): PropState[] {
  const fireflies = firefliesOf(state, player);
  for (let rank = 0; fireflies.length < count; rank += 1) {
    if (!fireflies.some((firefly) => firefly.rank === rank)) {
      fireflies.push(newFirefly(state, player, rank));
    }
  }
  if (fireflies.length <= count) {
    return fireflies;
  }
  const out = fireflies
    .filter((firefly) => firefly.phase === 'held')
    .sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0))
    .slice(0, fireflies.length - count);
  if (out.length === 0) {
    return fireflies;
  }
  const props = state.props ?? [];
  keepWhere(props, (prop) => !out.includes(prop));
  if (props.length === 0) {
    delete state.props;
  }
  return fireflies.filter((firefly) => !out.includes(firefly));
}

function newFirefly(state: SimState, player: PlayerState, rank: number): PropState {
  const made: PropState = {
    id: state.nextEntityId,
    kind: FIREFLY,
    playerId: player.id,
    phase: 'held',
    x: player.x,
    y: player.y,
    prevX: player.x,
    prevY: player.y,
    vx: 0,
    vy: 0,
    damage: 0,
    ticksLeft: 0,
    hitsLeft: 0,
    rank,
  };
  state.nextEntityId += 1;
  (state.props ??= []).push(made);
  return made;
}

function turnOf(rank: number, first: number, count: number): number {
  return wrap(rank - first, count);
}

function stingsComing(fireflies: readonly PropState[]): Map<number, number> {
  const coming = new Map<number, number>();
  for (const firefly of fireflies) {
    if (firefly.phase === 'flying' && firefly.targetId !== undefined) {
      coming.set(firefly.targetId, (coming.get(firefly.targetId) ?? 0) + firefly.damage);
    }
  }
  return coming;
}

export function preyOf(
  enemies: readonly EnemyState[],
  player: PlayerState,
  reach: number,
): EnemyState[] {
  const near: { enemy: EnemyState; distance: number }[] = [];
  for (const enemy of enemies) {
    const away = reach + enemy.radius;
    const distance = distanceSquared(enemy, player);
    if (enemy.hp > 0 && distance <= away * away) {
      near.push({ enemy, distance });
    }
  }
  return near
    .sort((a, b) => a.distance - b.distance || a.enemy.id - b.enemy.id)
    .map(({ enemy }) => enemy);
}

function attackOf(ctx: StepContext, owner: PlayerState): SwarmAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'swarm') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no swarm`);
  }
  return attack;
}

// Twice the flight: a bad vibe running away never holds a firefly long.
export function flightTicks(distance: number, speed: number): number {
  return Math.ceil((2 * distance) / speed);
}

export function sting(
  ctx: StepContext,
  prop: PropState,
  flight: Pick<SwarmAttack, 'range' | 'speed'>,
): void {
  const { state } = ctx;
  let target = state.enemies.find((enemy) => enemy.id === prop.targetId && enemy.hp > 0);
  if (target === undefined) {
    target = nearestUnstung(state, prop, flight.range);
    if (target !== undefined) {
      prop.targetId = target.id;
    }
  }
  prop.ticksLeft -= 1;
  if (target === undefined || prop.ticksLeft <= 0) {
    flyHome(prop);
    return;
  }
  const from: Vec2 = { x: prop.x, y: prop.y };
  if (!closeIn(prop, target, target.radius, flight.speed)) {
    return;
  }
  hurtEnemy(state, target, prop.damage, prop.playerId, from);
  (prop.hitIds ??= []).push(target.id);
  prop.hitsLeft -= 1;
  const next = prop.hitsLeft > 0 ? nearestUnstung(state, prop, flight.range) : undefined;
  if (next === undefined) {
    flyHome(prop);
    return;
  }
  prop.targetId = next.id;
  prop.ticksLeft = flightTicks(flight.range, flight.speed);
}

function nearestUnstung(state: SimState, prop: PropState, range: number): EnemyState | undefined {
  let nearest: EnemyState | undefined;
  let best = range * range;
  for (const enemy of state.enemies) {
    if (enemy.hp <= 0 || prop.hitIds?.includes(enemy.id) === true) {
      continue;
    }
    const distance = distanceSquared(enemy, prop);
    if (distance <= best) {
      nearest = enemy;
      best = distance;
    }
  }
  return nearest;
}

export function flyHome(prop: PropState): void {
  prop.phase = 'returning';
  delete prop.targetId;
  delete prop.hitIds;
}
